import {
  ComponentPropsWithoutRef,
  ComponentType,
  ElementRef,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"

import CloseIcon from "@/assets/icons/close/bold.svg?react"
import { cn } from "@/lib/utils"
import { useGetParentCompanyAndTenantQuery } from "@/store/companyDetailsAPI.ts"

import { SheetContext } from "./sheetContext.tsx"

const SheetRoot = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = forwardRef<
  ElementRef<typeof SheetPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-neutral-text/75 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed overflow-auto z-50 gap-4 bg-background shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full max-w-[95%] border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        right:
          "inset-y-0 right-0 h-full max-w-[95%] border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
)

interface SheetContentProps
  extends ComponentPropsWithoutRef<typeof SheetPrimitive.Content>, VariantProps<typeof sheetVariants> {
  sheetTitle?: string
  sheetDescription?: string
}

const SheetContent = forwardRef<ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ side = "right", className, children, sheetTitle, sheetDescription, ...props }, ref) => (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Title>{sheetTitle}</SheetPrimitive.Title>
      <SheetPrimitive.Description>{sheetDescription}</SheetPrimitive.Description>
      <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
        {children}
        <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
          <CloseIcon className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  ),
)
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = forwardRef<
  ElementRef<typeof SheetPrimitive.Title>,
  ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn("text-lg font-semibold text-foreground", className)} {...props} />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = forwardRef<
  ElementRef<typeof SheetPrimitive.Description>,
  ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export const SheetLabel = ({ label, subLabel, tag }: { label: string; subLabel: string; tag?: string }) => (
  <div className={"p-6"}>
    <div className={"mb-2 flex items-center gap-2"}>
      <h1 className={"text-2xl font-medium leading-8 text-neutral-title"}>{label}</h1>
      {tag && (
        <span className="rounded-lg bg-neutral-border px-2 py-1 text-base font-medium text-neutral-title">{tag}</span>
      )}
    </div>
    <h2 className={"text-neutral-text"}>{subLabel}</h2>
  </div>
)

export const SheetCompanyLabel = () => {
  const { data } = useGetParentCompanyAndTenantQuery()
  const { t } = useTranslation()
  const countryName = data?.company?.countryOfRegistration?.name || ""
  const countries = t("countries", { returnObjects: true }) as Record<string, string>
  const translatedCountry = countryName ? (countries[countryName] ?? countryName) : ""

  return (
    <SheetLabel
      label={data?.company?.name || ""}
      subLabel={`${translatedCountry} · ${data?.company?.registrationNumber || ""}`}
    />
  )
}

const Sheet = ({
  children,
  className,
  Content,
  asChild,
}: {
  children: ReactNode
  className?: string
  Content: ComponentType
  asChild?: boolean
}) => {
  const [open, setOpen] = useState(false)

  return (
    <SheetContext.Provider value={{ open, setOpen }}>
      <SheetRoot open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild={asChild} className={className}>
          {children}
        </SheetTrigger>
        <SheetContent>
          <Content />
        </SheetContent>
      </SheetRoot>
    </SheetContext.Provider>
  )
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetRoot,
  SheetTitle,
  SheetTrigger,
}
